import { flow, getEnv, getSnapshot, Instance, types } from "mobx-state-tree";
import { Video } from "../inspection/Video";
import { FileStates } from "../shop/ShopHistoryStorage";
import { endpoints } from "../api/ApiEndpoints";
import { mainInfoStorage } from "./MainInfoStorageModel";
import { inspectionInfoStorage } from "./InspectionInfoStorageModel";
import { videoPlayerService } from "../video-player/VideoPlayerService";
import { emptyRect, Rect, RectType } from "../../models/Rect";
import { DetectionModel } from "../../models/DetectionModel";
import { ServiceModel } from "../app/ServiceStorage";
import jsPDF from "jspdf";
import { Pdf } from "../../components/pdf/PdfCreator";

interface ILabel {
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  mainTimestamp: number,
  detectionTimestamp: number
}

interface IMainInformation {
    key: string,
    value: string
}

interface IDialog {
    screenshot: string,
    coordinates: any,
    timestamp: number
}

interface IDialogs {
    main: IDialog,
    detections: IDialog,
    distance: IDialog
}

const emptyDialog: IDialog = {
    screenshot: "",
    coordinates: null,
    timestamp: 0
}

const emptyDialogs: IDialogs = {
    main: emptyDialog,
    detections: emptyDialog,
    distance: emptyDialog
}

export interface OcrInput {
  detectMainInformation: boolean;
  detectMainInformationAtSeconds: number;
  mainInformationCoordinates: string;
  distanceInfoCoordinates: string;
  startInspectionDetectionFromSeconds: number;
  endInspectionDetectionAtSeconds: number;
  inspectionDetectionCoordinates: string;
}

const DialogModel = types.model({
    screenshot: types.string,
    coordinates: types.frozen<any>(),
    timestamp: types.number
})

const DialogsModel = types.model({
    main: DialogModel,
    detections: DialogModel,
    distance: DialogModel
})

export const OcrServiceModel = types.compose(
    ServiceModel,
    types.model({
        mainInformation: types.optional(types.array(types.frozen<IMainInformation>()), []),
        detections: types.optional(types.array(DetectionModel), []),
        screenshot: types.optional(types.frozen<string>(), ""),
        dialogs: types.optional(DialogsModel, emptyDialogs),
        pdfLoading: types.optional(types.frozen<boolean>(), false)
    })
).actions((self) => ({
  setCoordinates(rectType: RectType, label: ILabel, timestamp: number) {
    if (label === null) 
      self.dialogs[rectType].coordinates = null;
    else {
      self.dialogs[rectType].timestamp = timestamp;
      self.dialogs[rectType].coordinates = {
        ...emptyRect,
        x: label.startX,
        y: label.startY,
        w: label.endX - label.startX,
        h: label.endY - label.startY
      }
    }
  }
})).extend(self => ({
    actions: {
        clear() {
            self.fileId = undefined;
            self.video = undefined;
            self.status = FileStates.Failed
        },
        request: flow(function* () {
            var endpoint = endpoints.get("media_file_ocr");
            endpoint.routeParams = [self.fileId!];

            const response = yield getEnv(self).api.get(endpoint);
            self.video = new Video(response.video);
            self.status = self.getFileState(response.ocrWasSuccessful, response.ocrErrorMessage, response.ocrIsInProgress);
            self.mainInformation = response.mainInformation ?? [];
            self.detections = response.detections ?? [];
            self.setCoordinates("main", response.mainLabel, response.mainTimestamp);
            self.setCoordinates("detections", response.detectionLabel, response.detectionTimestamp);
            self.setCoordinates("distance", response.distanceLabel, response.detectionTimestamp);

            mainInfoStorage.setMainInformation(getSnapshot(self.mainInformation));
            inspectionInfoStorage.setDetections(getSnapshot(self.detections));
        }),
        runService: flow(function* () {
            var endpoint = endpoints.get("media_file_ocr");
            endpoint.routeParams = [self.fileId!];
      
            const request: OcrInput = {
                detectMainInformation: true,
                detectMainInformationAtSeconds: self.dialogs.main.timestamp,
                mainInformationCoordinates: mapCoordinates(self.dialogs.main.coordinates),
                distanceInfoCoordinates: mapCoordinates(self.dialogs.distance.coordinates),
                startInspectionDetectionFromSeconds: self.dialogs.detections.timestamp,
                endInspectionDetectionAtSeconds: videoPlayerService.videoDuration,
                inspectionDetectionCoordinates: mapCoordinates(self.dialogs.detections.coordinates)
            }
            yield getEnv(self).api.put(endpoint, request);
        }),
        redo: () => {
          self.status = FileStates.None
        },
        runAfterUpload: flow(function* () {
          var endpoint = endpoints.get("media_file_ocr");
          endpoint.routeParams = [self.fileId!];

          yield getEnv(self).api.put(endpoint, null);
          yield self.load();
        })
    }
})).views((self) => ({
    get inputProvided() {
        return this.mainInfoDone && this.detectionsDistanceDone;
    },
    get mainInfoDone() {
        return self.dialogs.main.coordinates !== null;
    },

    get detectionsDistanceDone() {
        return (
            self.dialogs.detections.coordinates !== null && self.dialogs.distance.coordinates !== null
        );
    },
    get ocrNotStarted(): boolean {
      return self.status === FileStates.None;
    },
    get ocrFailed(): boolean {
      return self.status === FileStates.Failed;
    },
    get ocrRunning(): boolean {
      return self.status === FileStates.Running;
    },
    get ocrSuccessful(): boolean {
      return self.status === FileStates.Success;
    }
})).actions((self) => {
    const downloadOcr = flow(function*() {
        const endpoint = endpoints.get("media_file_ocr_export");
        endpoint.routeParams = [self.fileId!];
        const response = yield getEnv(self).api.download(endpoint);
        return response.data;
    })
    return {
        setPdfLoading(loading: boolean) {
          self.pdfLoading = loading;
        },
        setScreenshotUrl(url: string) {
          self.screenshot = url;
        },
        setDialogScreenshot(rectTypes: RectType[], url: string) {
          rectTypes.forEach((rectType: RectType) => {
            self.dialogs[rectType].screenshot = url;
          });
        },
        setDialogCoordinates(rectTypes: RectType[], rects: Map<RectType, Rect>) {
          rectTypes.forEach((rectType: RectType) => {
            self.dialogs[rectType].coordinates = rects.get(rectType);
          });
        },
        setDialogTimestamp(rectTypes: RectType[], timestamp: number) {
          rectTypes.forEach((rectType: RectType) => {
            self.dialogs[rectType].timestamp = timestamp;
          });
        },
        getRects(rectTypes: RectType[]) {
          let rects: Map<RectType, Rect> = new Map();
  
          rectTypes.forEach((rectType: RectType) => {
            if (self.dialogs[rectType].coordinates) {
              let { x, y, h, w } = self.dialogs[rectType].coordinates;
              let rect: Rect = { x: x, y: y, h: h, w: w, selected: false, visible: true };
              rects.set(rectType, rect);
            }
          });
          return rects;
        },
        async downloadCsv() {
          self.setLoading(true);
          const blob = await downloadOcr();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'ocr.zip';
          a.click();
          URL.revokeObjectURL(url);
          self.setLoading(false);
        },
        async downloadPdf() {
          this.setPdfLoading(true);
          const pdf = new jsPDF('p', 'mm', 'a4');
          var file = new Pdf(
            pdf.internal.pageSize.getWidth(), 
            pdf.internal.pageSize.getWidth(), 
            10,
            pdf
          );

          const tables = document.querySelectorAll("#pdf-content-table")
          await file.addElements(tables);
          const cards = document.querySelectorAll("#pdf-content-card");
          await file.addElements(cards);

          file.save(`inspection_report.pdf`);
          this.setPdfLoading(false);
        }
    }
})

function mapCoordinates(coordinates: Rect) {
    return `${coordinates.x} ${coordinates.y} ${coordinates.x + coordinates.w} ${
      coordinates.y + coordinates.h
    }`;
  }

export type OcrService = Instance<typeof OcrServiceModel>